/**
 * Public Visitor Details page.
 *
 * Where it's used: this is the URL embedded inside the QR code printed on
 * every visitor badge. When a security guard, host, or any staff member
 * scans the badge, they land here and immediately see who the person is,
 * who they're here to see, and whether they're currently within their valid
 * visit window. Public on purpose (no auth) — the PIN itself is the secret.
 *
 *   /visitors/details?pin=482963
 *   /visitors/details/482963
 */
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
    IdentificationCard, User, Clock, MapPin, Buildings, Phone, Briefcase, CheckCircle, Warning,
} from "@phosphor-icons/react";

const STATUS_DISPLAY = {
    expected: { label: "Expected", c: "bg-indigo-50 text-indigo-700 border-indigo-200" },
    waiting: { label: "Waiting", c: "bg-amber-50 text-amber-700 border-amber-200" },
    notified: { label: "Host notified", c: "bg-blue-50 text-blue-700 border-blue-200" },
    checked_in: { label: "Checked in", c: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    checked_out: { label: "Checked out", c: "bg-gray-100 text-gray-600 border-gray-200" },
    blocked: { label: "Blocked", c: "bg-red-50 text-red-700 border-red-200" },
};

function formatTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
}

export default function VisitorDetails() {
    const { pin: paramPin } = useParams();
    const [search] = useSearchParams();
    const pin = paramPin || search.get("pin");
    const [visitor, setVisitor] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!pin || !/^\d{4,8}$/.test(pin)) {
            setError("Invalid or missing PIN");
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const { data } = await api.get(`/visitors/details-public/${pin}`);
                setVisitor(data);
            } catch (e) {
                setError(formatApiErrorDetail(e?.response?.data?.detail) || "Visitor not found");
            } finally {
                setLoading(false);
            }
        })();
    }, [pin]);

    const expired = visitor?.valid_until && new Date(visitor.valid_until) < new Date();
    const status = visitor ? (STATUS_DISPLAY[visitor.status] || STATUS_DISPLAY.waiting) : null;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-[#0B0D10] dark:to-[#0F1419] flex flex-col">
            <header className="border-b border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-[#0B0D10]/70 backdrop-blur-sm">
                <div className="max-w-2xl mx-auto px-6 py-4 flex items-center gap-2">
                    <IdentificationCard size={26} weight="duotone" color="#0055FF" />
                    <div>
                        <div className="label-eyebrow text-gray-500">TekWissen · EZ Workplace</div>
                        <div className="font-display font-semibold text-lg text-gray-900 dark:text-gray-100">Visitor Details</div>
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-2xl w-full mx-auto p-6">
                {loading && (
                    <Card className="p-12 text-center text-gray-400" data-testid="visitor-details-loading">
                        Loading visitor details…
                    </Card>
                )}

                {!loading && error && (
                    <Card className="p-8 border-red-200 dark:border-red-900 border-2 bg-red-50 dark:bg-red-950/20" data-testid="visitor-details-error">
                        <div className="flex items-center gap-3">
                            <Warning size={28} weight="fill" className="text-red-600" />
                            <div>
                                <div className="font-display font-semibold text-gray-900 dark:text-gray-100">Couldn&apos;t load visitor</div>
                                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{error}</div>
                            </div>
                        </div>
                    </Card>
                )}

                {!loading && visitor && (
                    <Card className="bg-white dark:bg-[#0B0D10] border-gray-200 dark:border-gray-800 p-6 sm:p-8" data-testid="visitor-details-card">
                        {/* Top row: photo + name + status */}
                        <div className="flex flex-col sm:flex-row gap-5">
                            <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 grid place-items-center shrink-0 mx-auto sm:mx-0">
                                {visitor.photo_data_url ? (
                                    <img
                                        src={visitor.photo_data_url}
                                        alt={visitor.name}
                                        className="w-full h-full object-cover"
                                        data-testid="visitor-details-photo"
                                    />
                                ) : (
                                    <User size={56} weight="duotone" className="text-gray-300 dark:text-gray-700" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0 text-center sm:text-left">
                                <div className="label-eyebrow text-gray-500">Visitor</div>
                                <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 leading-tight mt-1" data-testid="visitor-details-name">
                                    {visitor.name}
                                </h1>
                                {visitor.company && (
                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1.5 justify-center sm:justify-start" data-testid="visitor-details-company">
                                        <Buildings size={14} weight="duotone" /> {visitor.company}
                                    </div>
                                )}
                                <div className="mt-3 flex items-center gap-2 flex-wrap justify-center sm:justify-start">
                                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider border ${status.c}`} data-testid="visitor-details-status">
                                        <CheckCircle size={12} weight="fill" />
                                        {status.label}
                                    </span>
                                    {expired && (
                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider border bg-red-50 text-red-700 border-red-200" data-testid="visitor-details-expired">
                                            Badge expired
                                        </span>
                                    )}
                                    {visitor.nda_signed_at && (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-200">
                                            NDA ✓
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4 mt-7 pt-6 border-t border-gray-100 dark:border-gray-900">
                            <Field
                                icon={<User size={16} weight="duotone" />}
                                label={visitor.kind === "pre_registered" ? "Invited by" : "Visiting"}
                                value={visitor.host_name || "—"}
                                testId="visitor-details-host-name"
                            />
                            <Field
                                icon={<MapPin size={16} weight="duotone" />}
                                label="Host room"
                                value={visitor.host_room_name}
                                testId="visitor-details-host-room"
                            />
                            <Field
                                icon={<Clock size={16} weight="duotone" />}
                                label="Visit time"
                                value={formatTime(visitor.expected_at)}
                                testId="visitor-details-visit-time"
                            />
                            <Field
                                icon={<Clock size={16} weight="duotone" />}
                                label="Valid until"
                                value={formatTime(visitor.valid_until)}
                                testId="visitor-details-valid-until"
                            />
                            {visitor.purpose && (
                                <Field
                                    icon={<Briefcase size={16} weight="duotone" />}
                                    label="Purpose"
                                    value={visitor.purpose}
                                    testId="visitor-details-purpose"
                                />
                            )}
                            {visitor.phone && (
                                <Field
                                    icon={<Phone size={16} weight="duotone" />}
                                    label="Phone"
                                    value={visitor.phone}
                                />
                            )}
                        </div>

                        <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-900 flex items-center justify-between text-xs text-gray-500">
                            <div>PIN <span className="font-mono text-gray-800 dark:text-gray-200">{visitor.pin}</span></div>
                            {visitor.checked_in_at && (
                                <div>Checked in at <span className="text-gray-700 dark:text-gray-300">{formatTime(visitor.checked_in_at)}</span></div>
                            )}
                        </div>
                    </Card>
                )}
            </main>
        </div>
    );
}

function Field({ icon, label, value, testId }) {
    return (
        <div className="flex items-start gap-2.5">
            <div className="mt-0.5 text-[#0055FF]">{icon}</div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
                <div className="text-sm text-gray-900 dark:text-gray-100 mt-0.5 break-words" data-testid={testId}>{value}</div>
            </div>
        </div>
    );
}
